import React, { useState, useEffect } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import Button from '../ui/Button';
import { Loader2, Mail, Lock, ArrowRight, Gift } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const AuthPage: React.FC = () => {
    const navigate = useNavigate();
    const search = useSearch({ from: '/auth' }) as { mode?: string; redirect?: string };
    const [isLogin, setIsLogin] = useState(search.mode !== 'register');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [referralCode, setReferralCode] = useState<string | null>(null);

    // Check for stored referral code
    useEffect(() => {
        const storedRefCode = localStorage.getItem('progeodata_referral_code');
        if (storedRefCode) {
            setReferralCode(storedRefCode);
            // If coming from referral, default to register
            setIsLogin(false);
        }
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const endpoint = isLogin ? '/auth/login' : '/auth/register';
            const bodyData: any = { email, password };

            // Include referral code for registration
            if (!isLogin && referralCode) {
                bodyData.referralCode = referralCode;
            }

            const res = await fetch(`${API_URL}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bodyData),
            });

            const data = await res.json() as any;

            if (!res.ok) {
                throw new Error(data.error || 'Authentication failed');
            }

            // Store token and user info
            localStorage.setItem('progeodata_token', data.access_token);
            if (data.user?.api_key) {
                localStorage.setItem('progeodata_api_key', data.user.api_key);
            }
            localStorage.setItem('progeodata_user', JSON.stringify(data.user));

            // Clean up referral code after successful registration
            if (!isLogin) {
                localStorage.removeItem('progeodata_referral_code');
            }

            // Redirect
            const redirectTo = search.redirect || '/dashboard';
            navigate({ to: redirectTo });
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
            <div className="max-w-md w-full">
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-white mb-2">
                        {isLogin ? 'Welcome Back' : 'Create Account'}
                    </h1>
                    <p className="text-slate-400">
                        {isLogin
                            ? 'Sign in to access your credits and search leads'
                            : 'Register to get started with ProGeoData'}
                    </p>
                </div>

                {/* Referral Bonus Banner */}
                {!isLogin && referralCode && (
                    <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center gap-3">
                        <Gift className="w-6 h-6 text-emerald-400 flex-shrink-0" />
                        <div>
                            <p className="text-emerald-400 font-bold text-sm">Referral Bonus Active</p>
                            <p className="text-slate-300 text-xs">You'll get 50 bonus lookups when you create your account!</p>
                        </div>
                    </div>
                )}

                <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8">
                    {error && (
                        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                                Email
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                                    placeholder="you@company.com"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                                Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                                    placeholder={isLogin ? 'Enter password' : 'Create password (min 8 chars)'}
                                    minLength={8}
                                    required
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            variant="primary"
                            className="w-full"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    {isLogin ? 'Signing in...' : 'Creating account...'}
                                </>
                            ) : (
                                <>
                                    {isLogin ? 'Sign In' : (referralCode ? 'Create Account & Claim Bonus' : 'Create Account')}
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </>
                            )}
                        </Button>
                    </form>

                    {/* Free tier info for registration */}
                    {!isLogin && (
                        <div className="mt-4 p-3 bg-slate-900 rounded-lg text-center">
                            <p className="text-slate-400 text-xs">
                                Free plan: <span className="text-white font-bold">25 lookups/month</span> + <span className="text-white font-bold">5 CSV exports</span>
                                {referralCode && <> + <span className="text-emerald-400 font-bold">50 bonus lookups</span></>}
                            </p>
                            <p className="text-slate-500 text-xs mt-1">No credit card required</p>
                        </div>
                    )}

                    <div className="mt-6 text-center">
                        <button
                            onClick={() => setIsLogin(!isLogin)}
                            className="text-sky-400 hover:text-sky-300 text-sm"
                        >
                            {isLogin
                                ? "Don't have an account? Register Free"
                                : 'Already have an account? Sign in'}
                        </button>
                    </div>
                </div>

                <p className="text-center text-slate-500 text-sm mt-6">
                    By continuing, you agree to our{' '}
                    <a href="/terms" className="text-sky-400 hover:underline">Terms</a>
                    {' '}and{' '}
                    <a href="/privacy" className="text-sky-400 hover:underline">Privacy Policy</a>
                </p>
            </div>
        </div>
    );
};

export default AuthPage;
